// @ts-strict-ignore
import { useQuery } from '@apollo/client';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Queries } from '@shared/api';
import { View } from '@shared/components/View';
import { LinkedCard } from '@shared/components/blocks/lib/cards';
import { CardSelectItem } from '@shared/components/blocks/lib/inputCards/cardSelectItem';
import { NoCards } from '@shared/components/blocks/lib/inputCards/noCards';
import { QuickCreateCard } from '@shared/components/blocks/lib/inputCards/quickCreateCard';
import { BottomSheetContext } from '@shared/components/bottomSheet';
import { MBEmptyListPlaceholder } from '@shared/components/list/emptyListPlaceholder';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBMultiSelect } from '@shared/components/multiSelect';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';

interface Props {
  onPress(cards: LinkedCard[]): void;
  onAdd(query: string): void;
  cardId: string;
  channelId: number;
  channelName: string;
}

export function InCardsSheet({ onPress, cardId, channelId, channelName, onAdd }: Props) {
  const bottomSheetAPI = useContext(BottomSheetContext);
  const isDesktop = useIsDesktop();
  const [keyword, _setKeyword] = useState<string>('');
  const [setKeyword] = useDebouncedCallback(_setKeyword, 100);
  const [searchResults, setSearchResults] = useState<LinkedCard[]>([]);

  const { data, loading } = useQuery<
    MBAPI.ListRelatedCardsQuery,
    MBAPI.ListRelatedCardsQueryVariables
  >(Queries.card.listRelatedCards, {
    variables: {
      input: {
        id: cardId,
        channelId,
        type: 'IN',
        statuses: ['ACTIVE'],
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (data?.listRelatedCards) {
      const cards = data.listRelatedCards
        .map(val => ({ cardId: val.id, name: val.name }) as LinkedCard)
        .filter(c => c.name.toLowerCase().includes(keyword.toLowerCase()));
      setSearchResults(cards);
    }
  }, [keyword, data]);

  useEffect(() => {
    if (isDesktop) {
      bottomSheetAPI.refreshHeight?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, searchResults]);

  const renderItem = useCallback((card: LinkedCard) => <CardSelectItem card={card} />, []);

  const renderEmptyState = useCallback(
    (query: string) => {
      return (
        <View paddingVertical={3} paddingHorizontal={2} center>
          {query.length > 0 && (
            <MBEmptyListPlaceholder
              title={i18n('card.blocks.inCards.search.emptyState.title', { keyword: query })}
              description={i18n('card.blocks.inCards.search.emptyState.message')}
            />
          )}
          {query.length === 0 && !loading && <NoCards />}
          {query.length === 0 && loading && <MBLoadingIndicator />}
        </View>
      );
    },
    [loading],
  );

  const match = useCallback((card: LinkedCard, query: string, exact: boolean) => {
    if (exact) {
      return card.name === query;
    }
    return true;
  }, []);

  const renderCreateNew = useCallback(
    (query: string) => {
      return <QuickCreateCard channelName={channelName} query={query} />;
    },
    [channelName],
  );

  const onCreate = useCallback((query: string) => Promise.resolve(onAdd(query)), [onAdd]);

  const hasMore = false; // TODO: when query returns pagination data, make this dynamic

  return (
    <MBMultiSelect<LinkedCard>
      onChange={onPress}
      renderCreateNew={renderCreateNew}
      onCreate={onCreate}
      keyExtractor={card => card.cardId}
      max={1}
      match={match}
      renderItem={renderItem}
      renderEmptyState={renderEmptyState}
      values={searchResults}
      selectedValues={[]}
      onSearch={setKeyword}
      helpText={hasMore ? i18n('card.blocks.cardsInput.cardListLimitHelp') : undefined}
    />
  );
}
